import { StandardEvents } from '@shopify/events';
import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';
import { IbdEvents, IbdCylindoModelSelectedEvent } from '@theme/ibd-events';
import { Slideshow } from '@theme/slideshow';
import { getIOSVersion } from '@theme/utilities';

/** @typedef {import('@shopify/events').ProductSelectEvent} ProductSelectEvent */
/** @typedef {import('@theme/zoom-dialog').ZoomDialog} ZoomDialog */
/** @typedef {import('@theme/ibd-events').IbdCylindoViewerSettings} Settings */
/** @typedef {import('@theme/ibd-events').IbdProductUpdateEvent} IbdProductUpdateEvent */
/** @typedef {import('@theme/ibd-cylindo-events').CylindoViewerFullscreenChangedEvent} CylindoViewerFullscreenChangedEvent */

/** @typedef {CylindoViewerElement} CylindoViewer */

/** @type {Settings} */
const defaultSettings = {
  code: '',
  configuration: '',
  enable_idle_animation: true,
  idle_animation_delay: 0,
};

class IbdCylindoSlideAnchor extends Component {
  /** @type {HTMLButtonElement|undefined} */
  #anchorButton;

  /** @type {HTMLButtonElement|undefined} */
  #returnButton;

  /** @type {number|undefined} */
  #cylindoSlideIndex;

  /** @type {number} */
  #previousSlideIndex = 0;

  /** @type {Slideshow|undefined} */
  #slideshowComponent;

  constructor() {
    super();
  }

  /**
   * Checks if an index is valid.
   * @param {any} index
   * @returns {boolean}
   */
  #isValidIndex(index) {
    return index !== null && index !== undefined && Number.isInteger(index) && index >= 0;
  }

  /**
   * Handles the click event.
   * @param {MouseEvent} event
   */
  #handleAnchorClick(event) {
    event.preventDefault();

    if (!this.#slideshowComponent) {
      console.error('IBD | CylindoSlideAnchor | #handleAnchorClick | No slideshow component found');
      this.#hide();
      return;
    }

    if (this.#cylindoSlideIndex === undefined || !this.#isValidIndex(this.#cylindoSlideIndex)) {
      console.error('IBD | CylindoSlideAnchor | #handleAnchorClick | Invalid slide index');
      this.#hide();
      return;
    }

    void this.#slideshowComponent.select(this.#cylindoSlideIndex, event, { animate: true });
  }

  /**
   * Handles the return click event.
   * @param {MouseEvent} event
   */
  #handleReturnClick(event) {
    event.preventDefault();

    if (!this.#slideshowComponent) {
      console.error('IBD | CylindoSlideAnchor | #handleReturnClick | No slideshow component found');
      this.#hide();
      return;
    }

    void this.#slideshowComponent.select(this.#previousSlideIndex, event, { animate: true });
  }

  /**
   * Handles the slideshow select event.
   * Param is widened to `Event` because the upstream `SlideshowSelectEvent.eventName`
   * constant is typed as `string` (not a literal), so `addEventListener` cannot infer
   * the specific event type. Upstream files are not ours to fix.
   * @param {Event} event
   */
  #handleSlideshowSelect(event) {
    const { detail } = /** @type {SlideshowSelectEvent} */ (event);

    if (!this.#anchorButton) {
      console.error('IBD | CylindoSlideAnchor | #handleSlideshowSelect | No button found');
      return;
    }

    if (!this.#isValidIndex(detail.index)) {
      console.error('IBD | CylindoSlideAnchor | #handleSlideshowSelect | Invalid slide index');
      return;
    }

    if (detail.index === this.#cylindoSlideIndex) {
      this.classList.add('ibd-cylindo-slide-anchor--is-active');
    } else {
      this.classList.remove('ibd-cylindo-slide-anchor--is-active');
    }

    this.#previousSlideIndex = detail.previousIndex;
  }

  #hide() {
    this.hidden = true;
  }

  connectedCallback() {
    super.connectedCallback();

    const anchorButton = this.querySelector('[data-anchor-button]');
    if (!(anchorButton instanceof HTMLButtonElement)) {
      console.error('IBD | CylindoSlideAnchor | #connectedCallback | No button found');
      this.#hide();
      return;
    }

    const returnButton = this.querySelector('[data-return-button]');
    if (!(returnButton instanceof HTMLButtonElement)) {
      console.error('IBD | CylindoSlideAnchor | #connectedCallback | No button found');
      this.#hide();
      return;
    }

    const slideshowComponent = this.closest('slideshow-component');
    if (!(slideshowComponent instanceof Slideshow)) {
      console.error('IBD | CylindoSlideAnchor | #connectedCallback | No slideshow component found');
      this.#hide();
      return;
    }

    const cylindoSlideIndex = parseInt(this.dataset.slideIndex ?? '0', 10);
    if (!this.#isValidIndex(cylindoSlideIndex)) {
      console.error('IBD | CylindoSlideAnchor | #connectedCallback | Invalid slide index');
      this.#hide();
      return;
    }

    this.#anchorButton = anchorButton;
    this.#returnButton = returnButton;
    this.#slideshowComponent = slideshowComponent;
    this.#cylindoSlideIndex = cylindoSlideIndex;

    this.#anchorButton.addEventListener('click', this.#handleAnchorClick.bind(this));
    this.#returnButton.addEventListener('click', this.#handleReturnClick.bind(this));
    document.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect.bind(this));
  }
}

if (!customElements.get('ibd-cylindo-slide-anchor')) {
  customElements.define('ibd-cylindo-slide-anchor', IbdCylindoSlideAnchor);
}

/**
 * A custom element that manages the Cylindo viewer.
 * @extends {Component}
 */
class IbdCylindoViewer extends Component {
  /** @type {CylindoViewer|undefined} */
  viewer;

  /** @type {HTMLSelectElement|undefined} */
  modelSelect;

  /** @type {Settings} */
  settings = defaultSettings;

  /** @type {MutationObserver|null} */
  settingsObserver = null;

  /** @type {boolean} */
  userHasInteracted = false;

  /** @type {number|null} */
  animationLoop = null;

  /** @type {number} */
  viewerItemIndex = 0;

  /** @type {any[]} */
  items = [];

  /** @type {HTMLElement|null} */
  thumbnailBar = null;

  /** @type {HTMLButtonElement|null} */
  arButton = null;

  /** @type {HTMLButtonElement|null} */
  shortcut360Button = null;

  /** @type {HTMLButtonElement|null} */
  shortcut3dButton = null;

  /** @type {IntersectionObserver|null} */
  #intersectionObserver = null;

  /** @type {boolean} */
  #hasTriggeredInitialAnimation = false;

  /** @type {((event: Event) => void)|null} */
  #boundStartIdleAnimation = null;

  /** @type {((event: Event) => void)|null} */
  #boundStopIdleAnimation = null;

  /** @type {((event: Event) => void)|null} */
  #boundHandleModelSelect = null;

  /** @type {((event: CylindoViewerFullscreenChangedEvent) => void)|null} */
  #boundHandleFullscreenChange = null;

  /** @type {((event: IbdCylindoModelSelectedEvent) => void)|null} */
  #boundHandleModelSelected = null;

  /** @type {((event: Event) => void)|null} */
  #boundHandleItemsChange = null;

  /** @type {((event: Event) => void)|null} */
  #boundHandleItemChange = null;

  /** @type {((event: MouseEvent) => void)|null} */
  #boundHandleThumbnailClick = null;

  /** @type {((event: MouseEvent) => void)|null} */
  #boundHandleArClick = null;

  /** @type {((event: MouseEvent) => void)|null} */
  #boundHandleOpen360Click = null;

  /** @type {((event: MouseEvent) => void)|null} */
  #boundHandleOpen3dClick = null;

  constructor() {
    super();
  }

  disconnectedCallback() {
    this.#stopIdleAnimation();
    this.#disconnectIntersectionObserver();
    this.#unbindEventListeners();
    this.#removeThumbnailBar();
  }

  connectedCallback() {
    super.connectedCallback();

    const viewer = this.querySelector('cylindo-viewer');
    if (!viewer) {
      console.error('IBD | Cylindo | #connectedCallback | No viewer found');
      return;
    }

    this.viewer = viewer;

    try {
      const settings = JSON.parse(this.dataset.settings ?? '{}');
      this.settings = { ...defaultSettings, ...settings };
    } catch (error) {
      console.error('IBD | Cylindo | #connectedCallback | Error parsing settings');
      console.error(error);
    }

    const modelSelect = this.querySelector('[data-cylindo-model-select]');
    if (modelSelect instanceof HTMLSelectElement) {
      this.modelSelect = modelSelect;
    }

    const arButton = this.querySelector('[data-ibd-cylindo-viewer-ar-button]');
    if (arButton instanceof HTMLButtonElement) {
      this.arButton = arButton;
    }

    const shortcut360Button = document.querySelector('[data-ibd-cylindo-viewer-shortcut-view360-button]');
    if (shortcut360Button instanceof HTMLButtonElement) {
      this.shortcut360Button = shortcut360Button;
    }

    const shortcut3dButton = document.querySelector('[data-ibd-cylindo-viewer-shortcut-view3d-button]');
    if (shortcut3dButton instanceof HTMLButtonElement) {
      this.shortcut3dButton = shortcut3dButton;
    }

    document.addEventListener('DOMContentLoaded', this.#bindEventListeners.bind(this));
    this.#setupIntersectionObserver();
    this.#setupThumbnailBar();
  }

  /**
   * Handles the event when the user has selected a new model.
   * @param {Event} event
   */
  #handleModelSelect(event) {
    if (!this.viewer) {
      console.error('IBD | Cylindo | #handleModelSelect | No viewer found');
      return;
    }

    if (!(event.target instanceof HTMLSelectElement)) {
      console.error('IBD | Cylindo | #handleModelSelect | No valid target found');
      return;
    }

    this.dispatchEvent(new IbdCylindoModelSelectedEvent(event.target.value));
  }

  /**
   * Handles the event when a new model has been selected.
   * @param {IbdCylindoModelSelectedEvent} event
   * @returns
   */
  #handleModelSelected(event) {
    if (!event.detail.code || event.detail.code === this.settings.code) return;
    this.viewer?.setAttribute('code', event.detail.code);
  }

  /**
   * Handles a product select (variant change) event on the product page.
   * In-card variant changes are not relevant to the Cylindo viewer, so events
   * originating from within a product-card are ignored here.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (event.target instanceof Element && event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        this.#applyVariantHtml(detail.html);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('IBD | Cylindo Viewer | Event promise rejected:', error);
      });
  };

  /**
   * Handles an IBD product update event (combined-listing product switch).
   *
   * @param {IbdProductUpdateEvent} event - The IBD product update event.
   */
  #handleProductUpdated = (event) => {
    const html = event.detail?.data?.html;
    if (!html) return;
    this.#applyVariantHtml(html);
  };

  /**
   * Applies updated Cylindo settings from a freshly fetched document.
   *
   * @param {Document} html - The parsed HTML document with updated variant/product data.
   */
  #applyVariantHtml(html) {
    /** @type {HTMLElement|null} */
    const newElement = html.querySelector('ibd-cylindo-viewer');
    if (!newElement) {
      console.error('IBD | Cylindo Viewer | #applyVariantHtml | Cylindo viewer element not found in html data');
      return;
    }

    try {
      const newSettings = JSON.parse(newElement.dataset.settings ?? '{}');

      if (newSettings.configuration !== this.settings.configuration) {
        this.viewer?.setAttribute('configuration', newSettings.configuration);
      }

      if (newSettings.code !== this.settings.code && this.viewer) {
        this.viewer.setAttribute('code', newSettings.code);
        this.viewer.code = newSettings.code;
      }

      this.settings = { ...this.settings, ...newSettings };
    } catch (error) {
      console.error('IBD | Cylindo Viewer | #handleVariantUpdate', error);
    }
  }

  /**
   * Handles the fullscreen change event.
   *
   * This is a workaround for the lack of a comprehensive Full Screen API in iOS.
   * In iOS, the Cylindo Viewer will manage the fullscreen by changing its z-index,
   * with the help of the --z-index-ios-fullscreen custom property.
   *
   * However, our header also has a z-index --layer-sticky (which is around 8).
   * For some reason, even when the --z-index-ios-fullscreen is above --layer-sticky,
   * our header will still be on top of the viewer. Maybe due to Cylindo's shadow-root?
   *
   * So we need to temporarily unset the z-index of the header when the viewer is fullscreen.
   *
   * Read more: https://pdp.cylindo.com/API/cylindo-viewer#--z-index-ios-fullscreen
   * @param {CylindoViewerFullscreenChangedEvent} event
   */
  #handleFullscreenChange(event) {
    if (!this.viewer || typeof event.detail !== 'boolean' || !getIOSVersion()) return;

    const header = document.querySelector('#header-group header');
    if (!(header instanceof HTMLElement)) return;

    const isFullscreen = event.detail;
    if (isFullscreen) header.style.setProperty('z-index', 'unset');
    else header.style.removeProperty('z-index');
  }

  /**
   * Handles the AR click event.
   * @param {MouseEvent} event
   */
  #handleArClick(event) {
    event.preventDefault();

    if (!this.arButton) {
      console.error('IBD | CylindoViewer | #handleArClick | No AR button found');
      return;
    }

    if (!this.viewer) {
      console.error('IBD | CylindoViewer | #handleArClick | No viewer found');
      return;
    }

    this.viewer.ar = !this.viewer.ar;
  }

  /**
   * Handles the open 360 click event.
   * @param {MouseEvent} event
   */
  #handleOpen360Click(event) {
    event.preventDefault();

    if (!this.viewer) {
      console.error('IBD | CylindoViewer | #handleOpen360Click | No viewer found');
      return;
    }

    const item = this.viewer.items[0];
    this.viewer.item = { viewerItem: item, viewerItemIndex: 0 };

    this.#syncZoomDialogThumbnail();
  }

  /**
   * Handles the open 3D click event.
   * @param {MouseEvent} event
   */
  #handleOpen3dClick(event) {
    event.preventDefault();

    if (!this.viewer) {
      console.error('IBD | CylindoViewer | #handleOpen3dClick | No viewer found');
      return;
    }

    const item = this.viewer.items[1];
    this.viewer.item = { viewerItem: item, viewerItemIndex: 1 };

    this.#syncZoomDialogThumbnail();
  }

  /**
   * Sets up the Intersection Observer to trigger the initial animation
   * when the component enters the viewport.
   */
  #setupIntersectionObserver() {
    if (this.#hasTriggeredInitialAnimation) return;

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.#hasTriggeredInitialAnimation) {
            this.#hasTriggeredInitialAnimation = true;
            this.#startInitialAnimation();
            this.#disconnectIntersectionObserver();
          }
        });
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.8, // Trigger when the component is 80% visible
      }
    );

    this.#intersectionObserver.observe(this);
  }

  /**
   * Disconnects the Intersection Observer used to trigger the initial animation
   * when the component enters the viewport.
   */
  #disconnectIntersectionObserver() {
    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }
  }

  /**
   * Starts the initial animation, which is a full spin of the 360° model.
   * Used to visually indicate that the user can spin the model.
   */
  async #initialAnimation() {
    if (!this.viewer || this.userHasInteracted || this.viewer.ar) return;
    this.viewer.store.dispatch('frame/animate', { from: -32, to: this.viewer.frame });
  }

  #startInitialAnimation() {
    if (!this.viewer || this.userHasInteracted || this.viewer.ar) return;

    if (this.viewer.loaded) {
      void this.#initialAnimation();
      return;
    }

    this.viewer.addEventListener('loaded', this.#initialAnimation.bind(this), { once: true });
  }

  async #startIdleAnimation() {
    const viewer = this.viewer;
    if (!viewer || this.userHasInteracted || viewer.ar || viewer.modelViewer) {
      this.#stopIdleAnimation();
      return;
    }

    const start = viewer.frame;
    const mid = start - 3;
    const end = start + 1;

    const animateToNextFrame = async () => {
      const viewer = this.viewer;
      if (!viewer || this.userHasInteracted || viewer.ar || viewer.modelViewer) {
        this.#stopIdleAnimation();
        return;
      }

      this.classList.add('ibd--is-animating-360');
      viewer.store.dispatch('frame/animate', { from: start, to: mid });

      await new Promise((resolve) => setTimeout(resolve, 600));
      const viewerAfter1 = this.viewer;
      if (!viewerAfter1 || this.userHasInteracted || viewerAfter1.ar || viewerAfter1.modelViewer) {
        this.#stopIdleAnimation();
        return;
      }

      viewerAfter1.store.dispatch('frame/animate', { from: mid, to: end });

      await new Promise((resolve) => setTimeout(resolve, 800));
      const viewerAfter2 = this.viewer;
      if (!viewerAfter2 || this.userHasInteracted || viewerAfter2.ar || viewerAfter2.modelViewer) {
        this.#stopIdleAnimation();
        return;
      }

      viewerAfter2.store.dispatch('frame/animate', { from: end, to: start });
      this.classList.remove('ibd--is-animating-360');
    };

    this.animationLoop = setInterval(animateToNextFrame.bind(this), 4000);
  }

  #stopIdleAnimation() {
    if (!this.animationLoop) return;
    clearTimeout(this.animationLoop);
    this.animationLoop = null;
  }

  #bindViewerEventListeners() {
    const viewer = this.viewer;
    if (!viewer) return;

    this.#boundStartIdleAnimation = () => this.#startIdleAnimation();
    viewer.addEventListener('interaction-ready', this.#boundStartIdleAnimation, { once: true });

    this.#boundStopIdleAnimation = () => this.#stopIdleAnimation();
    viewer.addEventListener('spin-start', this.#boundStopIdleAnimation, { once: true });
    viewer.addEventListener('zoom-change', this.#boundStopIdleAnimation, { once: true });
  }

  /**
   * Sets up the custom thumbnail bar.
   */
  #setupThumbnailBar() {
    const viewer = this.viewer;
    if (!viewer) return;

    // Find existing thumbnail bar element from Liquid template
    this.thumbnailBar = this.querySelector('[slot="thumbnail-bar"]');
    if (!this.thumbnailBar) {
      console.warn('IBD | Cylindo Viewer | #setupThumbnailBar | No thumbnail bar found');
      return;
    }

    // Initialize items and viewerItemIndex
    this.items = viewer.items || [];
    this.viewerItemIndex = viewer.item?.viewerItemIndex ?? 0;

    // Set up event delegation for thumbnail clicks (single listener on the container)
    const handleThumbnailClick = this.#handleThumbnailClick.bind(this);
    this.#boundHandleThumbnailClick = handleThumbnailClick;
    this.thumbnailBar.addEventListener('click', handleThumbnailClick);

    // Update thumbnail bar initially
    this.#updateThumbnailBar();

    // Set up event listeners
    this.#boundHandleItemsChange = (event) => {
      /** @type {CustomEvent<any[]>} */
      const customEvent = /** @type {CustomEvent<any[]>} */ (event);
      this.items = customEvent.detail || [];
      this.#updateThumbnailBar();
    };

    this.#boundHandleItemChange = (event) => {
      /** @type {CustomEvent<{index?: number}>} */
      const customEvent = /** @type {CustomEvent<{index?: number}>} */ (event);
      this.viewerItemIndex = customEvent.detail?.index ?? 0;
      this.#updateThumbnailBar();
    };

    const handleItemsChange = this.#boundHandleItemsChange;
    const handleItemChange = this.#boundHandleItemChange;
    if (viewer.loaded && handleItemsChange && handleItemChange) {
      viewer.addEventListener('items-change', handleItemsChange);
      viewer.addEventListener('item-change', handleItemChange);
    } else {
      viewer.addEventListener(
        'loaded',
        () => {
          const v = this.viewer;
          if (!v) return;
          this.items = v.items || [];
          this.viewerItemIndex = v.item?.viewerItemIndex ?? 0;
          this.#updateThumbnailBar();
          if (handleItemsChange) v.addEventListener('items-change', handleItemsChange);
          if (handleItemChange) v.addEventListener('item-change', handleItemChange);
        },
        { once: true }
      );
    }
  }

  /**
   * Handles thumbnail button clicks using event delegation.
   * @param {MouseEvent} event
   */
  #handleThumbnailClick(event) {
    if (!event.target || !(event.target instanceof Element) || !this.viewer) return;

    const button = event.target.closest('[data-ibd-cylindo-viewer-thumbnail]');
    if (!button || !(button instanceof HTMLElement)) return;

    const itemType = button.dataset.ibdCylindoViewerThumbnail;
    const itemIndex = this.items.findIndex((item) => item.type === itemType);

    if (itemIndex !== -1) {
      const item = this.items[itemIndex];
      this.viewer.item = {
        viewerItem: item,
        viewerItemIndex: itemIndex,
      };
    }

    this.#syncZoomDialogThumbnail();
  }

  /**
   * Syncs the zoom-dialog thumbnail strip when this Cylindo viewer is inside a zoom dialog
   * (e.g. in product media gallery). Keeps the dialog's scroll-hint thumbnail in sync with
   * the active 360/3D view.
   */
  #syncZoomDialogThumbnail() {
    /** @type {ZoomDialog | null} */
    const zoomDialog = this.closest('zoom-dialog');
    if (!zoomDialog || !zoomDialog.handleThumbnailClick) return;

    /** @type {HTMLElement | null} */
    const mediaLi = this.closest('li[data-cylindo-viewer-zoomed-thumbnail]');
    if (!mediaLi || !(mediaLi instanceof HTMLElement)) return;

    const mediaIndex = parseInt(mediaLi.dataset.mediaIndex ?? '', 10);
    if (isNaN(mediaIndex)) return;

    void zoomDialog.handleThumbnailClick(mediaIndex);
  }

  /**
   * Updates the thumbnail bar with current items.
   */
  #updateThumbnailBar() {
    if (!this.thumbnailBar) return;

    // Find all thumbnail buttons
    /** @type {NodeListOf<HTMLButtonElement>} */
    const thumbnails = this.thumbnailBar.querySelectorAll('[data-ibd-cylindo-viewer-thumbnail]');

    // Update each thumbnail button's state based on current items
    this.items.forEach((item, index) => {
      const isSelected = this.viewerItemIndex === index;
      const button = Array.from(thumbnails).find(
        (thumbnail) => thumbnail.dataset.ibdCylindoViewerThumbnail === item.type
      );
      if (!button) return;

      // Update selected state
      if (isSelected) {
        button.classList.add('ibd-cylindo-viewer__main-action--selected');
      } else {
        button.classList.remove('ibd-cylindo-viewer__main-action--selected');
      }

      // Update ARIA attributes
      button.setAttribute('aria-label', `Select ${item.type || `item ${index + 1}`}`);
      button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });
  }

  /**
   * Removes the thumbnail bar.
   */
  #removeThumbnailBar() {
    // Remove event listener from thumbnail bar
    if (this.thumbnailBar && this.#boundHandleThumbnailClick) {
      this.thumbnailBar.removeEventListener('click', this.#boundHandleThumbnailClick);
      this.#boundHandleThumbnailClick = null;
    }
    // Just clear the reference, don't remove the element since it's part of the template
    this.thumbnailBar = null;
  }

  #bindEventListeners() {
    if (this.viewer) {
      if (this.viewer.loaded) {
        this.#bindViewerEventListeners();
      } else {
        this.viewer.addEventListener('loaded', this.#bindViewerEventListeners.bind(this), { once: true });
      }
    }

    this.#boundHandleFullscreenChange = (event) => this.#handleFullscreenChange(event);
    this.viewer?.addEventListener('fullscreen-change', this.#boundHandleFullscreenChange);

    this.#boundHandleArClick = (event) => this.#handleArClick(event);
    this.arButton?.addEventListener('click', this.#boundHandleArClick);

    this.#boundHandleOpen360Click = (event) => this.#handleOpen360Click(event);
    this.shortcut360Button?.addEventListener('click', this.#boundHandleOpen360Click);

    this.#boundHandleOpen3dClick = (event) => this.#handleOpen3dClick(event);
    this.shortcut3dButton?.addEventListener('click', this.#boundHandleOpen3dClick);

    this.#boundHandleModelSelect = (event) => this.#handleModelSelect(event);
    this.modelSelect?.addEventListener('change', this.#boundHandleModelSelect);

    this.#boundHandleModelSelected = (event) => this.#handleModelSelected(event);
    this.addEventListener(IbdEvents.ibdCylindoModelSelected, this.#boundHandleModelSelected);

    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    document.addEventListener(IbdEvents.ibdProductUpdated, this.#handleProductUpdated);
  }

  #unbindEventListeners() {
    if (this.#boundStartIdleAnimation) {
      this.viewer?.removeEventListener('ready', this.#boundStartIdleAnimation);
      this.#boundStartIdleAnimation = null;
    }

    if (this.#boundStopIdleAnimation) {
      this.viewer?.removeEventListener('spin-start', this.#boundStopIdleAnimation);
      this.viewer?.removeEventListener('zoom-change', this.#boundStopIdleAnimation);
      this.#boundStopIdleAnimation = null;
    }

    if (this.#boundHandleFullscreenChange) {
      this.viewer?.removeEventListener('fullscreen-change', this.#boundHandleFullscreenChange);
      this.#boundHandleFullscreenChange = null;
    }

    if (this.#boundHandleArClick) {
      this.arButton?.removeEventListener('click', this.#boundHandleArClick);
      this.#boundHandleArClick = null;
    }

    if (this.#boundHandleOpen360Click) {
      this.shortcut360Button?.removeEventListener('click', this.#boundHandleOpen360Click);
      this.#boundHandleOpen360Click = null;
    }

    if (this.#boundHandleOpen3dClick) {
      this.shortcut3dButton?.removeEventListener('click', this.#boundHandleOpen3dClick);
      this.#boundHandleOpen3dClick = null;
    }

    if (this.#boundHandleModelSelect) {
      this.modelSelect?.removeEventListener('change', this.#boundHandleModelSelect);
      this.#boundHandleModelSelect = null;
    }

    if (this.#boundHandleModelSelected) {
      this.removeEventListener(IbdEvents.ibdCylindoModelSelected, this.#boundHandleModelSelected);
      this.#boundHandleModelSelected = null;
    }

    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    document.removeEventListener(IbdEvents.ibdProductUpdated, this.#handleProductUpdated);

    if (this.#boundHandleItemsChange) {
      this.viewer?.removeEventListener('items-change', this.#boundHandleItemsChange);
      this.#boundHandleItemsChange = null;
    }

    if (this.#boundHandleItemChange) {
      this.viewer?.removeEventListener('item-change', this.#boundHandleItemChange);
      this.#boundHandleItemChange = null;
    }
  }
}

if (!customElements.get('ibd-cylindo-viewer')) {
  customElements.define('ibd-cylindo-viewer', IbdCylindoViewer);
}
